import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, Zap, Wrench, Sparkles } from "lucide-react";
import { Price } from "@/components/pricing/Price";
import { SectionHeader } from "@/components/ui/section-header";
import { startPlanCheckout } from "@/lib/payments/checkoutSession";
import { ProviderHint } from "@/components/payments/ProviderHint";
import { useEntitlements } from "@/hooks/useEntitlements";
import {
  CONSUMER_PLANS,
  PLAN_ENTITLEMENTS,
  formatFCFA,
  annualSavingsPct,
  type ConsumerTierCode,
} from "@/config/entitlements";
import { toast } from "sonner";

type Interval = "monthly" | "annual";
type CurrencyDisplay = "EUR" | "FCFA";

// FCFA is mobile-money territory → no trial; bill first month immediately.
const MOBILE_MONEY_CURRENCIES = new Set<CurrencyDisplay>(["FCFA"]);

export default function Pricing() {
  const { t } = useTranslation();
  const ent = useEntitlements();
  const [searchParams] = useSearchParams();
  const highlightTier = (() => {
    const raw = searchParams.get("highlight");
    if (raw && ["free", "founder", "operator", "team"].includes(raw)) {
      return raw as ConsumerTierCode;
    }
    return null;
  })();
  useEffect(() => {
    if (!highlightTier) return;
    const el = document.querySelector<HTMLElement>(`[data-tier-card="${highlightTier}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightTier]);
  const [interval, setInterval] = useState<Interval>("monthly");
  const [currency, setCurrency] = useState<CurrencyDisplay>("EUR");
  const [coachingTier, setCoachingTier] = useState<"4h" | "8h" | "12h">("8h");

  const showFCFA = currency === "FCFA";
  const isCardRail = !MOBILE_MONEY_CURRENCIES.has(currency);

  const trialCta = t("marketing.pricing.ctaTrial", { defaultValue: "Start 7-day free trial" });
  const firstMonthCta = t("marketing.pricing.ctaFirstMonth", { defaultValue: "Start now — first month" });
  const freeCta = t("marketing.pricing.ctaFree", { defaultValue: "Get started free" });

  const formatPrice = (cents: number, suffix?: string) => {
    if (cents === 0) {
      if (showFCFA) {
        return (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold tracking-tight text-text-1">0 FCFA</span>
            {suffix && <span className="text-sm text-text-3">{suffix}</span>}
          </div>
        );
      }
      return <Price size="card" eurCents={0} />;
    }
    if (showFCFA) {
      return (
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold tracking-tight text-text-1">{formatFCFA(cents)}</span>
          {suffix && <span className="text-sm text-text-3">{suffix}</span>}
        </div>
      );
    }
    return <Price size="card" eurCents={cents} suffix={suffix} />;
  };

  const onCheckout = (planId: string) => {
    if (!ent.isAuthed) {
      toast.info(t("marketing.pricing.signInFirst", { defaultValue: "Please sign in to continue." }));
    }
    const providerOverride =
      typeof window !== "undefined" && sessionStorage.getItem("preferred_provider") === "flutterwave"
        ? ("flutterwave" as const)
        : undefined;
    startPlanCheckout(planId, { providerOverride }).catch((e) => {
      toast.error(
        t("payments.errors.unknown", { defaultValue: "Couldn't start checkout" }) +
          ": " +
          (e as Error).message,
      );
    });
  };

  const onChooseFallback = () => {
    sessionStorage.setItem("preferred_provider", "flutterwave");
    toast.success(
      t("payments.wave.fallbackChosen", {
        defaultValue: "Switched to Flutterwave for the next checkout.",
      }),
    );
  };

  // ---- Tier cards ----
  type TierCardSpec = {
    code: ConsumerTierCode;
    title: string;
    description: string;
    highlight?: boolean;
    badge?: string;
  };

  const tierCards: TierCardSpec[] = [
    {
      code: "free",
      title: t("marketing.pricing.tierFree", { defaultValue: "Free" }),
      description: t("marketing.pricing.freeDesc2", {
        defaultValue: "Diagnose, sample lessons, and your 4-week plan.",
      }),
    },
    {
      code: "founder",
      title: t("marketing.pricing.tierFounder", { defaultValue: "Founder Pass" }),
      description: t("marketing.pricing.founderDesc2", {
        defaultValue: "Everything for serious solo founders.",
      }),
      highlight: true,
      badge: t("marketing.pricing.popular", { defaultValue: "Most popular" }),
    },
    {
      code: "operator",
      title: t("marketing.pricing.tierOperator", { defaultValue: "Operator" }),
      description: t("marketing.pricing.operatorDesc", {
        defaultValue: "Unlimited KÔRA + Velyra CEO and 15% off everything platform-delivered.",
      }),
    },
    {
      code: "team",
      title: t("marketing.pricing.tierTeam", { defaultValue: "Team" }),
      description: t("marketing.pricing.teamDesc", {
        defaultValue: "5 seats included. Same Operator perks for your whole team.",
      }),
    },
  ];

  const featureLines = (tier: ConsumerTierCode): string[] => {
    const e = PLAN_ENTITLEMENTS[tier];
    const lines: string[] = [];
    lines.push(
      e.koraDailyMessageLimit === null
        ? t("marketing.pricing.feat.koraUnlimited", { defaultValue: "KÔRA — AI mentor (unlimited)" })
        : t("marketing.pricing.feat.koraLimit", {
            defaultValue: "KÔRA — {{n}} messages / day",
            n: e.koraDailyMessageLimit,
          }),
    );
    if (e.velyraEnabled) {
      lines.push(
        t("marketing.pricing.feat.velyra", { defaultValue: "Velyra CEO — AI strategist (unlimited)" }),
      );
    }
    lines.push(
      e.selfPacedLibrary === "full"
        ? t("marketing.pricing.feat.libraryFull", { defaultValue: "Full self-paced course library" })
        : t("marketing.pricing.feat.librarySamples", {
            defaultValue: "Sample lessons + à-la-carte bootcamps",
          }),
    );
    lines.push(
      e.community === "full"
        ? t("marketing.pricing.feat.communityFull", { defaultValue: "Full community access" })
        : t("marketing.pricing.feat.communityRead", { defaultValue: "Community (read-only)" }),
    );
    if (e.officeHours === "facilitated_group") {
      lines.push(
        t("marketing.pricing.feat.officeFacilitated", {
          defaultValue: "Facilitated group office hours",
        }),
      );
    } else if (e.officeHours === "group") {
      lines.push(t("marketing.pricing.feat.officeGroup", { defaultValue: "Monthly group office hours" }));
    }
    if (e.recordings) {
      lines.push(t("marketing.pricing.feat.recordings", { defaultValue: "All bootcamp recordings" }));
    }
    if (e.certificates) {
      lines.push(t("marketing.pricing.feat.certs", { defaultValue: "Completion certificates" }));
    }
    lines.push(
      e.resourceLibrary === "unlimited"
        ? t("marketing.pricing.feat.resourcesAll", { defaultValue: "Unlimited templates & tools" })
        : t("marketing.pricing.feat.resources3", { defaultValue: "3 free downloads" }),
    );
    if (e.premiumPlaybooks) {
      lines.push(t("marketing.pricing.feat.playbooks", { defaultValue: "Premium operator playbooks" }));
    }
    if (e.platformDiscountPct > 0) {
      lines.push(
        t("marketing.pricing.feat.discount", {
          defaultValue: "{{pct}}% off bootcamps, microservices & playbooks",
          pct: e.platformDiscountPct,
        }),
      );
    }
    if (e.seats > 1) {
      lines.push(
        t("marketing.pricing.feat.seats", { defaultValue: "{{n}} seats included", n: e.seats }),
      );
    }
    return lines;
  };

  const planIdFor = (code: ConsumerTierCode, iv: Interval): string | null => {
    if (code === "free") return null;
    return `${code}.${iv}`;
  };

  const ctaFor = (code: ConsumerTierCode): string => {
    if (code === "free") return freeCta;
    return isCardRail ? trialCta : firstMonthCta;
  };

  const priceFor = (code: ConsumerTierCode): { cents: number; suffix: string } => {
    const plan = CONSUMER_PLANS[code];
    const cents = interval === "annual" ? plan.annual : plan.monthly;
    const suffix =
      cents === 0
        ? ""
        : interval === "annual"
        ? t("marketing.pricing.perYear", { defaultValue: "/ year" })
        : t("marketing.pricing.perMonth", { defaultValue: "/ month" });
    return { cents, suffix };
  };

  const founderSavings = annualSavingsPct("founder");

  // ---- One-off products ----
  const bootcampTiers = [
    { key: "beg", name: t("marketing.pricing.bootcampBeginner", { defaultValue: "Beginner" }), priceCents: 9900, planId: "bootcamp.beginner" },
    { key: "int", name: t("marketing.pricing.bootcampIntermediate", { defaultValue: "Intermediate" }), priceCents: 17900, planId: "bootcamp.intermediate", highlight: true },
    { key: "adv", name: t("marketing.pricing.bootcampAdvanced", { defaultValue: "Advanced" }), priceCents: 24900, planId: "bootcamp.advanced" },
  ];

  // For display: real discount is applied server-side at checkout via resolvePrice().
  const operatorDiscountPct = PLAN_ENTITLEMENTS.operator.platformDiscountPct;

  const microservicePriceCents = 49900;

  const coachingPlans = {
    "4h":  { priceCents: 29900, hoursLabel: t("marketing.pricing.coaching.4h",  { defaultValue: "4 hours / month" }) },
    "8h":  { priceCents: 49900, hoursLabel: t("marketing.pricing.coaching.8h",  { defaultValue: "8 hours / month" }) },
    "12h": { priceCents: 79900, hoursLabel: t("marketing.pricing.coaching.12h", { defaultValue: "12 hours / month" }) },
  } as const;

  const faq = useMemo(
    () => [
      {
        q: t("marketing.pricing.faq.trialQ", { defaultValue: "Do I need a card for the trial?" }),
        a: t("marketing.pricing.faq.trialA", {
          defaultValue:
            "Yes — the 7-day free trial on Founder Pass and Operator requires a card on file and auto-converts at day 8 unless you cancel. Mobile-money sign-ups bill the first month immediately.",
        }),
      },
      {
        q: t("marketing.pricing.faq.discountQ", { defaultValue: "What does the 15% Operator/Team discount apply to?" }),
        a: t("marketing.pricing.faq.discountA", {
          defaultValue:
            "Bootcamps, microservices and premium playbooks — anything we deliver on the platform. It never applies to 1:1 coaching, Fractional Exec engagements, marketplace listings or institutional deals.",
        }),
      },
      {
        q: t("marketing.pricing.faq.mobileMoneyQ", { defaultValue: "Do you support mobile money?" }),
        a: t("marketing.pricing.faq.mobileMoneyA2", {
          defaultValue:
            "Yes — Wave for XOF (Senegal, Côte d'Ivoire and the rest of UEMOA), with Flutterwave as a fallback. Mobile-money subscriptions bill month-by-month with a renewal reminder.",
        }),
      },
      {
        q: t("marketing.pricing.faq.refundsQ", { defaultValue: "What's your refund policy?" }),
        a: t("marketing.pricing.faq.refundsA", {
          defaultValue:
            "Bootcamps: 14-day refund from cohort start. Subscriptions: cancel anytime, prorated refunds within 30 days of an annual purchase. Coaching: refund unused hours minus a 10% admin fee.",
        }),
        href: "/legal/refund-policy",
      },
    ],
    [t],
  );

  return (
    <PublicShell>
      <Seo
        title={t("marketing.pricing.seoTitle", { defaultValue: "Pricing — Bootpreneurs" })}
        description={t("marketing.pricing.seoDescription", {
          defaultValue: "Simple plans for solo founders, operators and teams. Start free.",
        })}
      />

      {/* Header + toggles */}
      <section className="border-b border-border-1">
        <div className="container py-16 md:py-20">
          <SectionHeader
            align="center"
            eyebrow={t("marketing.pricing.eyebrow", { defaultValue: "Pricing" })}
            title={t("marketing.pricing.title2", { defaultValue: "Pick the plan that fits today" })}
            lede={t("marketing.pricing.subtitle2", {
              defaultValue: "Start free. Upgrade only when you need more.",
            })}
          />

          <div className="mt-8 flex flex-col items-center gap-4">
            {/* Monthly / Annual */}
            <div className="inline-flex items-center rounded-full border border-border-1 bg-surface-2 p-1 text-sm">
              <button
                onClick={() => setInterval("monthly")}
                className={`rounded-full px-4 py-1.5 font-medium transition ${
                  interval === "monthly" ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                }`}
              >
                {t("marketing.pricing.monthly", { defaultValue: "Monthly" })}
              </button>
              <button
                onClick={() => setInterval("annual")}
                className={`rounded-full px-4 py-1.5 font-medium transition ${
                  interval === "annual" ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                }`}
              >
                {t("marketing.pricing.annual", { defaultValue: "Annual" })}
                <span className="ml-1.5 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-success">
                  {t("marketing.pricing.savePct", { defaultValue: "Save {{pct}}%", pct: founderSavings })}
                </span>
              </button>
            </div>

            {/* Currency */}
            <div className="inline-flex items-center rounded-full border border-border-1 bg-surface-2 p-1 text-xs">
              {(["EUR", "FCFA"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`rounded-full px-3 py-1 font-medium transition ${
                    currency === c ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            {showFCFA && (
              <p className="text-xs text-text-3">
                {t("marketing.pricing.fcfaNote", {
                  defaultValue:
                    "FCFA prices shown at the BCEAO fixed peg (1 € = 655.957 FCFA). Mobile-money sign-ups bill the first month immediately.",
                })}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 4-tier grid */}
      <section className="border-b border-border-1">
        <div className="container py-16">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {tierCards.map((card) => {
              const { cents, suffix } = priceFor(card.code);
              const pid = planIdFor(card.code, interval);
              return (
                <Card
                  key={card.code}
                  variant={card.highlight ? "feature" : "interactive"}
                  data-tier-card={card.code}
                  className={`relative overflow-hidden ${
                    highlightTier === card.code
                      ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : ""
                  }`}
                >
                  {card.badge && (
                    <div className="absolute right-0 top-0">
                      <div className="bg-primary px-4 py-1 t-eyebrow text-primary-foreground shadow-md">
                        {card.badge}
                      </div>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-xl">{card.title}</CardTitle>
                    <div className="mt-3">{formatPrice(cents, cents === 0 ? undefined : suffix)}</div>
                    <CardDescription className="mt-2">{card.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2">
                      {featureLines(card.code).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.25} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {card.code === "free" ? (
                      <Button asChild className="w-full" variant="brandOutline" size="lg">
                        <Link to="/diagnostic">{ctaFor(card.code)} →</Link>
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        variant={card.highlight ? "brand" : "brandOutline"}
                        size={card.highlight ? "xl" : "lg"}
                        onClick={() => pid && onCheckout(pid)}
                      >
                        {ctaFor(card.code)}
                      </Button>
                    )}
                    {card.code !== "free" && showFCFA && (
                      <ProviderHint onChooseFallback={onChooseFallback} />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="mt-8 text-center text-sm text-text-3">
            {isCardRail
              ? t("marketing.pricing.trialNote", {
                  defaultValue:
                    "Card required. Cancel before day 8 to avoid being charged.",
                })
              : t("marketing.pricing.firstMonthNote", {
                  defaultValue:
                    "Mobile-money: you'll be charged for the first month now; we'll remind you before each renewal.",
                })}
          </p>
        </div>
      </section>

      {/* Section — Bootcamps & cohorts */}
      <section className="border-b border-border-1 bg-surface-2/40">
        <div className="container py-16">
          <h2 className="mb-2 text-center t-display-sm text-text-1">
            {t("marketing.pricing.s2Title", { defaultValue: "Bootcamps & cohorts" })}
          </h2>
          <p className="mb-8 text-center t-body-sm text-text-3">
            {t("marketing.pricing.s2Sub2", {
              defaultValue:
                "4-week intensives with live sessions and a verified certificate. Operator & Team get 15% off.",
            })}
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {bootcampTiers.map((tier) => (
              <Card
                key={tier.key}
                variant={tier.highlight ? "feature" : "interactive"}
                className="relative overflow-hidden"
              >
                {tier.highlight && (
                  <div className="absolute right-0 top-0">
                    <div className="bg-primary px-4 py-1 t-eyebrow text-primary-foreground shadow-md">
                      {t("marketing.pricing.popular", { defaultValue: "Most popular" })}
                    </div>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-xl">
                    {t("marketing.pricing.bootcampPrefix", { defaultValue: "Bootcamp" })} — {tier.name}
                  </CardTitle>
                  <div className="mt-2">
                    {formatPrice(
                      tier.priceCents,
                      t("marketing.pricing.perCohort", { defaultValue: "/ cohort" }),
                    )}
                  </div>
                  <Badge variant="outline" className="mt-3 w-fit">
                    {t("marketing.pricing.discountEligible", {
                      defaultValue: "Operator/Team: -15%",
                    })}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    className="w-full"
                    variant={tier.highlight ? "brand" : "brandOutline"}
                    size={tier.highlight ? "xl" : "lg"}
                    onClick={() => onCheckout(tier.planId)}
                  >
                    {t("marketing.pricing.ctaEnroll", { defaultValue: "Enroll" })} →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section — Microservices & Coaching */}
      <section className="border-b border-border-1">
        <div className="container py-16">
          <h2 className="mb-8 text-center t-display-sm text-text-1">
            {t("marketing.pricing.s3Title", { defaultValue: "Hands-on help" })}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Microservices */}
            <Card variant="interactive" className="relative">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl">
                    {t("marketing.pricing.microTitle", { defaultValue: "Microservices" })}
                  </CardTitle>
                </div>
                <div className="mt-3">
                  {formatPrice(
                    microservicePriceCents,
                    t("marketing.pricing.fixedScope", { defaultValue: "fixed-scope" }),
                  )}
                </div>
                {ent.isAuthed && (
                  <p className="mt-2 text-xs text-success">
                    {t("marketing.pricing.subscriberPriceHint", {
                      defaultValue:
                        "Operator/Team see {{pct}}% off at checkout.",
                      pct: operatorDiscountPct,
                    })}
                  </p>
                )}
                <CardDescription className="mt-2">
                  {t("marketing.pricing.microDesc2", {
                    defaultValue: "Pre-priced deliverables shipped in days.",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full" variant="brandOutline" size="lg">
                  <Link to="/services/microservices">
                    {t("marketing.pricing.ctaBrowseMicro", { defaultValue: "Browse microservices" })} →
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Coaching */}
            <Card variant="interactive" className="relative">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl">
                    {t("marketing.pricing.coachingTitle", { defaultValue: "Coaching" })}
                  </CardTitle>
                </div>
                <div className="mt-3 inline-flex items-center rounded-full border border-border-1 bg-surface-2 p-1 text-xs">
                  {(["4h", "8h", "12h"] as const).map((k) => (
                    <button
                      key={k}
                      onClick={() => setCoachingTier(k)}
                      className={`rounded-full px-3 py-1 font-medium transition ${
                        coachingTier === k ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                      }`}
                    >
                      {k}/mo
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  {formatPrice(
                    coachingPlans[coachingTier].priceCents,
                    t("marketing.pricing.perMonth", { defaultValue: "/ month" }),
                  )}
                </div>
                <CardDescription className="mt-2">
                  {coachingPlans[coachingTier].hoursLabel} ·{" "}
                  {t("marketing.pricing.coachingDesc2", {
                    defaultValue: "1:1 with an operator-mentor.",
                  })}
                </CardDescription>
                <p className="mt-2 text-xs text-text-3">
                  {t("marketing.pricing.coachingNoDiscount", {
                    defaultValue: "Coaching is 1:1 and not eligible for the platform discount.",
                  })}
                </p>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full" variant="brandOutline" size="lg">
                  <Link to="/mentors">
                    {t("marketing.pricing.ctaFindCoach", { defaultValue: "Find a coach" })} →
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section — Institutional / Enterprise (Contact only) */}
      <section className="border-b border-border-1 bg-gradient-to-br from-primary/10 to-surface-1">
        <div className="container py-16">
          <Card variant="feature" className="mx-auto max-w-5xl">
            <CardContent className="grid gap-6 p-8 md:grid-cols-[1.2fr_1fr] md:items-center">
              <div>
                <span className="mb-3 inline-flex items-center gap-2 t-eyebrow text-primary">
                  <Sparkles className="h-4 w-4" />
                  {t("marketing.pricing.enterpriseEyebrow", { defaultValue: "Institutional & Enterprise" })}
                </span>
                <h2 className="t-display-sm text-text-1">
                  {t("marketing.pricing.enterpriseTitle", {
                    defaultValue: "Custom programs for organisations & ecosystems",
                  })}
                </h2>
                <p className="mt-2 text-text-3">
                  {t("marketing.pricing.enterpriseSub", {
                    defaultValue:
                      "Cohorts for accelerators, banks, ministries & corporates. White-label, custom playbooks, dedicated success.",
                  })}
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 md:items-end">
                <Button asChild size="xl" variant="brand">
                  <Link to="/enterprise">
                    {t("marketing.pricing.ctaTalkSales", { defaultValue: "Talk to sales" })} →
                  </Link>
                </Button>
                <p className="text-xs text-text-3 md:text-right">
                  {t("marketing.pricing.enterpriseNoSelfServe", {
                    defaultValue: "No self-serve checkout. We'll scope and quote together.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="container py-16">
          <h2 className="mb-8 text-center t-display-md text-text-1">
            {t("marketing.pricing.faqTitle", { defaultValue: "Frequently asked" })}
          </h2>
          <Accordion type="single" collapsible className="mx-auto max-w-3xl">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`}>
                <AccordionTrigger className="text-left t-body font-semibold text-text-1">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent>
                  {f.a}
                  {(f as { href?: string }).href && (
                    <>
                      {" "}
                      <Link
                        to={(f as { href: string }).href}
                        className="font-medium text-primary hover:underline"
                      >
                        {t("marketing.pricing.faq.readFullPolicy", {
                          defaultValue: "Read full policy →",
                        })}
                      </Link>
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </PublicShell>
  );
}
